// Saldo a Hoy — collections view. One row per reservation that currently owes
// money for time already consumed: saldo a hoy = max(0, rent accrued through
// today − paid). Rows are ordered by customer so a client's reservations sit
// together; the "Por Cliente" summary card gives each client's total.
// As-of is always today (no date filter). Optional vendor (asesor) dropdown.
//
// Debts marked as incobrable are excluded — they live in the Cuentas
// Incobrables report, which is the same RPC with p_scope flipped.
import supabase from "../../../supabase-client";
import { formatFriendlyDate, RESERVATION_STATUS_ORDER } from "../../../utility/funcs";

const fmtMoney = (v) => "$" + Math.round(Number(v) || 0).toLocaleString("es-MX");
const fmtDate = (v) => (v ? formatFriendlyDate(v) : "");

export const saldoAHoyReport = {
  id: "saldo-a-hoy",
  title: "Saldo a Hoy",
  description:
    "Reservas que deben dinero por el tiempo ya consumido (renta devengada hasta hoy − pagado), agrupadas por cliente.",
  category: "Finanzas",
  defaultSort: { key: "saldo_a_hoy", direction: "descending" },
  // Client-side "facet" filters — the report loads the full today-snapshot, then
  // these narrow it in place (include semantics, empty = all) without re-running.
  // Options are auto-derived from the loaded rows' column values.
  filters: [
    { type: "facet", key: "customer_name", label: "Cliente", column: "customer_name" },
    { type: "facet", key: "status", label: "Estatus", column: "status", order: RESERVATION_STATUS_ORDER },
    { type: "facet", key: "vendor_name", label: "Asesor", column: "vendor_name" },
  ],
  summary: [
    { type: "count", label: "Reservas con saldo" },
    { type: "sum", label: "Saldo a hoy total", valueKey: "saldo_a_hoy", valueFormat: fmtMoney },
    {
      type: "groupBy",
      label: "Por Cliente",
      key: "customer_name",
      valueKey: "saldo_a_hoy",
      valueFormat: fmtMoney,
      format: (v) => v || "Sin cliente",
    },
    {
      type: "groupBy",
      label: "Por Vendedor",
      key: "vendor_name",
      valueKey: "saldo_a_hoy",
      valueFormat: fmtMoney,
      format: (v) => v || "Sin vendedor",
    },
  ],
  columns: [
    // groupable: sorting by Cliente collapses each client's reservations under
    // a subheader with the per-client subtotals (the `subtotal` columns below).
    { key: "customer_name", label: "Cliente", width: 240, truncate: true, groupable: true, groupNoun: "reserva" },
    { key: "reservation_id", label: "Reserva", format: (v) => `#${v}` },
    { key: "car", label: "Auto", width: 220, truncate: true },
    { key: "status", label: "Estatus", sortOrder: RESERVATION_STATUS_ORDER },
    { key: "vendor_name", label: "Asesor" },
    // exportType "date" → XLSX gets real date cells (dd/mm/yyyy hh:mm), matching
    // the /reservations export; the on-screen table keeps the friendly format.
    { key: "start_date", label: "Entrega", format: fmtDate, exportType: "date" },
    { key: "end_date", label: "Devolución", format: fmtDate, exportType: "date" },
    { key: "total_price", label: "Total", format: fmtMoney, subtotal: true, exportType: "currency" },
    { key: "paid", label: "Pagado", format: fmtMoney, subtotal: true, exportType: "currency" },
    { key: "accrued_to_today", label: "Devengado a hoy", format: fmtMoney, subtotal: true, exportType: "currency" },
    { key: "saldo_a_hoy", label: "Saldo a hoy", format: fmtMoney, subtotal: true, exportType: "currency" },
  ],
  async fetch() {
    // Load the full today-snapshot; Cliente/Estatus/Asesor narrowing happens
    // client-side via the facet filters above.
    const { data, error } = await supabase.rpc("report_saldo_a_hoy", {
      p_vendor: null,
      p_scope: "cobrable",
    });
    if (error) throw error;
    return data || [];
  },
};
